import { Card, Form, Input, InputNumber, Select, Space, Switch } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '~/app/components/common/Button';
import type { InlineTaskFormValues } from '~/app/components/maintenance/preventive/wizard/StepTasks/types';
import { TaskSubtasksField } from '~/app/components/maintenance/TaskSubtasksField/TaskSubtasksField';

type StepTasksTaskBuilderProps = {
  workTypeOptions: Array<{ label: string; value: string }>;
  partOptions: Array<{ label: string; value: string }>;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (values: InlineTaskFormValues) => Promise<void>;
};

export function StepTasksTaskBuilder({
  workTypeOptions,
  partOptions,
  loading,
  onCancel,
  onSubmit,
}: StepTasksTaskBuilderProps) {
  const { t } = useTranslation('maintenance');
  const [form] = Form.useForm<InlineTaskFormValues>();

  return (
    <Card
      size="small"
      title={t('wizard.tasks.createTaskTitle')}
      extra={
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={() => {
            form.resetFields();
            onCancel();
          }}
          aria-label={t('wizard.cancel')}
        />
      }
      styles={{ body: { paddingTop: 12 } }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ mandatory: true, requiresShutdown: false }}
        onFinish={(values) => {
          void onSubmit(values).then(() => form.resetFields());
        }}
      >
        <Form.Item
          name="name"
          label={t('tasks.name')}
          rules={[{ required: true }]}
        >
          <Input placeholder={t('wizard.tasks.taskNamePlaceholder')} />
        </Form.Item>
        <Form.Item
          name="workTypeId"
          label={t('tasks.workType')}
          rules={[{ required: true }]}
        >
          <Select
            options={workTypeOptions}
            placeholder={t('tasks.selectWorkType')}
          />
        </Form.Item>
        <Space size={24} wrap style={{ marginBottom: 16 }}>
          <Form.Item
            name="mandatory"
            label={t('tasks.mandatory')}
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="requiresShutdown"
            label={t('tasks.requiresShutdown')}
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
        </Space>
        <Form.Item name="estimatedDurationMinutes" label={t('tasks.duration')}>
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="partIds" label={t('tasks.parts')}>
          <Select
            mode="multiple"
            allowClear
            options={partOptions}
            placeholder={t('tasks.selectParts')}
            optionFilterProp="label"
            showSearch
          />
        </Form.Item>
        <TaskSubtasksField />
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => {
              form.resetFields();
              onCancel();
            }}
          >
            {t('wizard.cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {t('wizard.tasks.addTask')}
          </Button>
        </Space>
      </Form>
    </Card>
  );
}
